import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Home, UserPlus, Baby, Trash2, Mail, BadgeCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* "Our Household" — Couple/Family member management (Part B).
   Owner invites adults by email (own logins); children = managed profiles (no login). */

export default function HouseholdManager() {
  const [data, setData] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [addingChild, setAddingChild] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [childName, setChildName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/household").then((r) => setData(r.data)).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const invite = async () => {
    if (!email.trim() || !name.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await api.post("/household/invite", { email, name });
      toast.success(`Invitation sent to ${email}`);
      setEmail(""); setName(""); setInviting(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send invitation"); }
    finally { setBusy(false); }
  };

  const addChild = async () => {
    if (!childName.trim()) return;
    setBusy(true);
    try {
      await api.post("/household/managed", { name: childName, relation: "Child" });
      toast.success(`${childName} added as a managed profile`);
      setChildName(""); setAddingChild(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add profile"); }
    finally { setBusy(false); }
  };

  const remove = async (mid) => {
    try { await api.delete(`/household/members/${mid}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  const seatsFull = data.seats_used >= data.seats;

  return (
    <div data-testid="household-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-heading font-900 text-[#4285F4] text-2xl flex items-center gap-2">
          <Home className="w-6 h-6" /> Our Household
          <span className="text-sm font-500 text-muted-foreground">({data.seats_used}/{data.seats} traveler profiles)</span>
        </h2>
        {data.is_owner && (
          <div className="flex gap-2">
            <button onClick={() => { setInviting((v) => !v); setAddingChild(false); }} disabled={seatsFull && !inviting}
              data-testid="household-invite-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-700 text-white text-sm font-600 hover:bg-indigo-600 disabled:opacity-50">
              <UserPlus className="w-4 h-4" /> Invite member
            </button>
            <button onClick={() => { setAddingChild((v) => !v); setInviting(false); }} disabled={seatsFull && !addingChild}
              data-testid="household-child-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-indigo-300 text-indigo-700 text-sm font-600 hover:bg-indigo-50 disabled:opacity-50">
              <Baby className="w-4 h-4" /> Add child profile
            </button>
          </div>
        )}
      </div>

      {data.is_owner && data.plan === null && data.status === "trial" && (
        <p className="text-sm text-muted-foreground mb-3">Your free trial includes up to {data.seats} traveler profiles so you can try household sharing. Keep it after your trial with a <Link to="/membership" className="text-indigo-700 font-600 hover:underline">Couple or Family plan</Link>.</p>
      )}
      {seatsFull && data.is_owner && (
        <p className="text-sm bg-amber-50 border border-amber-300 text-amber-900 px-3 py-2 mb-3" data-testid="household-seats-full">
          All {data.seats} traveler profiles are in use — <Link to="/membership" className="font-700 text-indigo-700 hover:underline">upgrade your plan</Link> to add more.
        </p>
      )}

      {inviting && (
        <div className="border border-indigo-300 bg-indigo-50/50 p-4 mb-4 flex flex-wrap gap-2" data-testid="household-invite-form">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Their name" data-testid="invite-name-input"
            className="flex-1 min-w-[140px] border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="their@email.com" data-testid="invite-email-input"
            className="flex-1 min-w-[180px] border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          <button onClick={invite} disabled={busy} data-testid="invite-send-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-700 text-white text-sm font-700 hover:bg-indigo-600 disabled:opacity-50">
            <Mail className="w-4 h-4" /> {busy ? "Sending…" : "Send invitation"}
          </button>
          <p className="w-full text-xs text-muted-foreground">They'll get an email, create (or sign in to) their own TravelerPulse account, and join your membership — each adult keeps a private login and profile.</p>
        </div>
      )}

      {addingChild && (
        <div className="border border-indigo-300 bg-indigo-50/50 p-4 mb-4 flex flex-wrap gap-2" data-testid="household-child-form">
          <input value={childName} onChange={(e) => setChildName(e.target.value)} placeholder="Child's name" data-testid="child-name-input"
            className="flex-1 min-w-[160px] border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          <button onClick={addChild} disabled={busy} data-testid="child-add-btn"
            className="px-4 py-2 bg-indigo-700 text-white text-sm font-700 hover:bg-indigo-600 disabled:opacity-50">Add profile</button>
          <p className="w-full text-xs text-muted-foreground">Managed profiles have no login — a parent keeps their documents, seats and packing lists. Convert to a full account later.</p>
        </div>
      )}

      {data.members.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {data.is_owner
            ? "It's just you so far — invite your spouse, family or add children's profiles to plan together."
            : `You're part of ${data.owner_name || "a"} household.`}
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.members.map((m) => (
            <div key={m.id} className="border border-zinc-300 bg-white p-4 flex items-start gap-3" data-testid={`household-member-${m.id}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-mono text-sm text-white shrink-0 ${m.role === "child" ? "bg-rose-500" : "bg-indigo-600"}`}>
                {(m.name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-700 text-sm truncate">{m.name}</div>
                <div className="text-xs text-muted-foreground truncate">{m.email || m.relation || "Managed profile"}</div>
                <div className="mt-1">
                  {m.role === "child"
                    ? <span className="inline-flex items-center gap-1 text-xs bg-rose-50 border border-rose-200 text-rose-700 px-1.5 py-0.5"><Baby className="w-3 h-3" /> Managed by parent</span>
                    : m.status === "active"
                      ? <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 px-1.5 py-0.5"><BadgeCheck className="w-3 h-3" /> Member</span>
                      : <span className="inline-flex items-center gap-1 text-xs bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5"><Clock className="w-3 h-3" /> Invited</span>}
                </div>
              </div>
              {data.is_owner && (
                <button onClick={() => remove(m.id)} data-testid={`household-remove-${m.id}`}
                  className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
